/**
 * @graphql agg
 */

import { graphql } from 'react-relay';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { reportError } from '../reportError.ts';

import type {
	blockServiceUserInfoQuery$data,
	blockServiceUserInfoQuery$variables,
} from './__generated__/blockServiceUserInfoQuery.graphql.ts';

export type UserInfoQueryData = {
	accountId: string;
	displayName: string | null | undefined;
	avatarUrl: string | null | undefined;
};

/**
 * Requests the user info for the given account ID
 *
 * All three properties (accountId, displayName, avatarURL) are marked as required within the Identity schema:
 * 		https://bitbucket.org/atlassian/profile-retrieval-service/src/061f452f0945ea7951ef050b89bee7734209de75/profile-retrieval-service-model/src/main/kotlin/io/atlassian/profileretrieval/model/User.kt#lines-38
 * These should never be undefined and yet are marked as optional by the agg schema.
 */
export const fetchUserInfo = async (accountId: string): Promise<UserInfoQueryData | undefined> => {
	try {
		const { data, errors } = await queryInterop<
			blockServiceUserInfoQuery$data,
			blockServiceUserInfoQuery$variables
		>({
			query: graphql`
				query blockServiceUserInfoQuery($accountId: ID!) {
					confluence_atlassianUser(current: false, id: $accountId) {
						accountId: id
						displayName
						photos {
							value
							isPrimary
						}
					}
				}
			`,
			variables: {
				accountId,
			},
		});

		errors?.forEach((error) => {
			logFetchError(error);
			markErrorAsHandled(error);
		});

		const confUser = data.confluence_atlassianUser;
		if (!confUser || !confUser.accountId) {
			return undefined;
		}

		const avatarUrl = confUser?.photos?.find((photo) => photo?.isPrimary)?.value;

		return {
			accountId,
			displayName: confUser.displayName,
			avatarUrl,
		};
	} catch (error) {
		logFetchError(error);
		markErrorAsHandled(error);
		return undefined;
	}
};

const logFetchError = (maybeError: Error | undefined) => {
	const error = maybeError instanceof Error ? maybeError : new Error(String(maybeError));
	const errorToLog = new Error(
		`Error when fetching Snippet owner user details: ${error?.message || JSON.stringify(error)}`,
	);
	reportError(errorToLog);
};
